'use client';

import Link from 'next/link';
import { ChevronRight } from 'lucide-react';
import { Typography } from '@/shared/ui/shadcn/typography';
import { cn } from '@/shared/lib/cn';
import { useDragScroll } from '@/shared/hooks/use-drag-scroll';

// Constants (matching Apple design tokens)
// Mirrors Apple `DesignTokens.PanelHeight.standard` (180) — section panels
// host concept/album cards with 4:3 aspect, leaving 24px breathing room.
export const PANEL_HEIGHT = 180;
export const CARD_ASPECT_RATIO = 4 / 3;

// Navigation Circle Button (teal circle with chevron arrow).
// Used on the dashboard; project detail sections use SectionChevron instead.
export function NavigationCircle({
  onClick,
}: {
  href?: string;
  onClick?: () => void;
}) {
  return (
    <span
      role="button"
      onClick={onClick}
      className="flex items-center justify-center w-6 h-6 rounded-full bg-primary hover:bg-primary/90 transition-colors flex-shrink-0"
    >
      <ChevronRight className="h-3.5 w-3.5 text-primary-foreground rtl:-scale-x-100" />
    </span>
  );
}

// Plain gray chevron for inline section titles (Apple HIG style).
// No circle background — sits right after the title and optional count.
export function SectionChevron() {
  return (
    <ChevronRight className="h-5 w-5 text-muted-foreground/60 rtl:-scale-x-100 flex-shrink-0" />
  );
}

// Section Header (used by Concepts, Albums — non-collapsible sections with carousel)
export interface SectionAction {
  label?: string;
  onClick?: () => void;
  href?: string;
}

interface SectionHeaderProps {
  title: string;
  actions?: SectionAction[];
  count?: number;
}

export function SectionHeader({
  title,
  actions = [],
  count,
}: SectionHeaderProps) {
  const action = actions[0];

  const content = (
    <div className="flex items-center gap-1.5 mb-2 cursor-pointer">
      <Typography variant="h6" className="font-semibold">
        {title}
      </Typography>
      {count != null && count > 0 && <ContextCount count={count} />}
      {action && <SectionChevron />}
    </div>
  );

  if (action?.href) {
    return <Link href={action.href}>{content}</Link>;
  }

  if (action?.onClick) {
    return (
      <button
        type="button"
        onClick={action.onClick}
        className="w-full text-left"
      >
        {content}
      </button>
    );
  }

  return content;
}

// Compact Section (always collapsed — shows title, context info, and navigation circle)
interface CompactSectionProps {
  title: string;
  href?: string;
  onClick?: () => void;
  contextContent?: React.ReactNode;
}

export function CompactSection({
  title,
  href,
  onClick,
  contextContent,
}: CompactSectionProps) {
  const content = (
    <div className="flex items-center gap-1.5 cursor-pointer">
      <Typography variant="h6" className="font-semibold flex-shrink-0">
        {title}
      </Typography>
      {contextContent && (
        <div className="min-w-0 flex items-center">{contextContent}</div>
      )}
      <SectionChevron />
    </div>
  );

  return (
    <div>
      {href ? (
        <Link href={href}>{content}</Link>
      ) : (
        <button type="button" onClick={onClick} className="w-full text-left">
          {content}
        </button>
      )}
      <div className="h-px bg-border mt-3" />
    </div>
  );
}

// Warning-tone indicator dot for required empty sections
export function RequiredIndicator() {
  return (
    <div className="w-2.5 h-2.5 rounded-full bg-warning flex-shrink-0" />
  );
}

// Context preview text (truncated to single line)
export function ContextPreview({ text }: { text: string }) {
  return <span className="text-list-item-subtitle text-muted-foreground truncate">{text}</span>;
}

// Context count badge
export function ContextCount({ count }: { count: number }) {
  return (
    <span className="text-list-item-subtitle text-muted-foreground flex-shrink-0">{count}</span>
  );
}

// Section Panel (fixed height, neutral background)
interface SectionPanelProps {
  children: React.ReactNode;
  onClick?: () => void;
  href?: string;
  className?: string;
}

export function SectionPanel({
  children,
  onClick,
  href,
  className,
}: SectionPanelProps) {
  const content = (
    <div
      className={cn(
        // Relative tint (5% of foreground), NOT a surface token: `bg-muted/50`
        // over the white page background was nearly invisible — empty-section
        // panels read as a blank gap instead of a structured tile.
        'rounded-xl bg-foreground/5 overflow-hidden',
        (onClick || href) &&
          'cursor-pointer hover:bg-foreground/8 transition-colors',
        className
      )}
      style={{ height: PANEL_HEIGHT }}
      onClick={onClick}
    >
      {children}
    </div>
  );

  if (href) {
    return <Link href={href}>{content}</Link>;
  }

  return content;
}

// Scrollable carousel — bleeds past the page horizontal padding so cards run off
// the right edge of the viewport (matching Apple `HorizontalCarousel` and the
// Android Compose `LazyRow` pattern). Page padding is `px-4 sm:px-6 lg:px-8` on
// the project page; negative margins here cancel that, and matching internal
// padding restores left-side alignment with the section header.
export function ScrollableCarousel({
  children,
}: {
  children: React.ReactNode;
}) {
  // Drag/wheel/touch swipe shared with the dashboard carousels. `hasDragged`
  // disables pointer events on the cards mid-drag so a swipe never fires a
  // card click, and `onDragStart` preventDefault kills native image dragging.
  const { ref, isMouseDown, hasDragged, handlers } = useDragScroll();

  return (
    <div
      ref={ref}
      className={cn(
        'overflow-x-auto scrollbar-hide select-none -mx-4 sm:-mx-6 lg:-mx-8',
        isMouseDown ? 'cursor-grabbing' : 'cursor-grab'
      )}
      {...handlers}
    >
      <div
        className={cn(
          'flex items-center gap-2 px-4 sm:px-6 lg:px-8 py-3',
          hasDragged && 'pointer-events-none'
        )}
      >
        {children}
      </div>
    </div>
  );
}

